import React from "react";
import { BadgeDelta, Card, type DeltaType, Title } from "@tremor/react";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";

import { type SalesFunnelCounts } from "~/modules/Dashboard/AppStats/salesFunnel/useSalesFunnelStats";

interface SalesFunnelCardProps {
  current: SalesFunnelCounts | null;
  previous: SalesFunnelCounts | null;
  /** Human label of the compared period, e.g. "1–4.08". */
  comparisonLabel: string;
  isLoading: boolean;
}

type FunnelStep = keyof SalesFunnelCounts;

const FUNNEL_STEPS: FunnelStep[] = ["leads", "meetings", "attended", "won"];

// One hue, light to dark: the funnel is ordinal, so the ramp carries "how deep" and nothing else.
// Blue steps 250–550 of the chart palette; the lightest still clears 2:1 against the card.
const STEP_COLORS: Record<FunnelStep, string> = {
  leads: "#86B6EF",
  meetings: "#5598E7",
  attended: "#2A78D6",
  won: "#1C5CAB",
};

/** A bar narrower than this stops reading as a bar, so a non-zero step never drops below it. */
const MIN_BAR_PERCENT = 3;

const formatNumber = (value: number) => Intl.NumberFormat("pl").format(value);

const percentOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : null);

const getDelta = (value: number, previous: number | null | undefined): number | null =>
  previous ? Math.round(((value - previous) / previous) * 100) : null;

const getDeltaType = (delta: number): DeltaType => {
  if (delta === 0) {
    return "unchanged";
  }
  return delta > 0 ? "increase" : "decrease";
};

export const SalesFunnelCard = ({
  current,
  previous,
  comparisonLabel,
  isLoading,
}: SalesFunnelCardProps): React.ReactElement => {
  const { t } = useTranslation("dashboard");

  const top = current ? Math.max(current.leads, current.meetings, current.attended, current.won) : 0;
  const overallConversion = current ? percentOf(current.won, current.leads) : null;

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-4">
        <Title>{t("salesFunnel.title")}</Title>
        {overallConversion !== null ? (
          <span className="rounded-tremor-full bg-tremor-background-subtle px-2.5 py-1 text-tremor-label text-tremor-content dark:bg-dark-tremor-background-subtle dark:text-dark-tremor-content">
            {t("salesFunnel.overallConversion")}{" "}
            <span className="tabular-nums font-semibold text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
              {overallConversion}%
            </span>
          </span>
        ) : null}
      </div>

      {isLoading && !current ? (
        <Skeleton active paragraph={{ rows: 8 }} className="mt-6" />
      ) : (
        <div className="mt-6 flex flex-1 flex-col justify-between gap-6">
          {FUNNEL_STEPS.map((step, index) => {
            const value = current?.[step] ?? 0;
            const previousValue = previous?.[step] ?? null;
            const previousStep = index > 0 ? FUNNEL_STEPS[index - 1] : null;
            const stepConversion = previousStep && current ? percentOf(value, current[previousStep]) : null;
            const delta = getDelta(value, previousValue);
            const barPercent = top > 0 && value > 0 ? Math.max(MIN_BAR_PERCENT, (value / top) * 100) : 0;

            return (
              <div key={step}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-tremor-default font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                    {t(`salesFunnel.${step}`)}
                  </span>
                  <span className="text-2xl font-semibold leading-none text-tremor-content-strong dark:text-dark-tremor-content-strong">
                    {formatNumber(value)}
                  </span>
                </div>

                <div className="mt-2.5 h-2 overflow-hidden rounded-tremor-full bg-tremor-background-subtle dark:bg-dark-tremor-background-subtle">
                  <div
                    className="h-full rounded-tremor-full"
                    style={{ width: `${barPercent}%`, backgroundColor: STEP_COLORS[step] }}
                  />
                </div>

                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-tremor-label text-tremor-content dark:text-dark-tremor-content">
                  <span>
                    {stepConversion !== null ? t("salesFunnel.stepConversion", { percent: stepConversion }) : " "}
                  </span>
                  {previousValue !== null ? (
                    <span className="flex items-center gap-2">
                      {delta !== null ? (
                        <BadgeDelta deltaType={getDeltaType(delta)} size="xs">
                          {delta > 0 ? "+" : ""}
                          {delta}%
                        </BadgeDelta>
                      ) : null}
                      <span>
                        {comparisonLabel}: <span className="tabular-nums">{formatNumber(previousValue)}</span>
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
